/**
 * Socket.io Client for Real-time Order Updates
 * Based on ADMIN_ORDER_INTEGRATION_GUIDE.md
 */

import { io, type Socket } from 'socket.io-client';
import type { API } from '@/services/ant-design-pro/typings';
import { getAuthToken } from '@/utils/authPersistence';

let socket: Socket | null = null;

export interface SocketOrderUpdate {
  orderId?: string;
  orderNumber?: string;
  progressStatus?: API.ProgressStatus;
  warehouseStatus?: API.WarehouseStatus;
  shippingStatus?: API.ShippingStatus;
  trackingNumber?: string;
  krTrack?: string;
  updatedAt?: string;
  updatedBy?: string;
}

export interface SocketUpdateOrderParams extends API.UpdateOrderParams {
  orderId?: string;
  orderNumber?: string;
}

export interface SocketBatchUpdateParams {
  orderIds: string[];
  progressStatus: API.ProgressStatus;
  sendSms?: boolean;
}

/**
 * Initialize socket connection
 */
export const initSocket = (token?: string): Socket => {
  // Reuse the existing instance even while it is (re)connecting: replacing it
  // would orphan every listener other components (header badges, order pages)
  // bound to the old instance — they would silently stop receiving events
  // until a full page refresh.
  if (socket) {
    if (!socket.connected && !socket.active) {
      socket.connect();
    }
    return socket;
  }

  // Prefer WebSocket (one persistent connection, fewer RTTs than long-polling).
  // Fall back to polling if WebSocket fails (proxies, corporate firewalls, some mobile networks).
  // Backend `socket.config.ts` already allows both transports.
  const SOCKET_URL = process.env.SOCKET_SERVER_URL;

  socket = io(SOCKET_URL, {
    path: '/socket.io',
    // Function form: re-read the token on every (re)connection attempt so a
    // refreshed login token is picked up without rebuilding the socket.
    auth: (cb) => {
      cb({ token: token || getAuthToken() || '' });
    },
    transports: ['websocket', 'polling'],
    withCredentials: true,
    reconnection: true,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 10000,
    // Never give up: with a finite attempt count the socket dies permanently
    // whenever the backend is unreachable longer than the retry window (e.g.
    // a deploy/restart), freezing all real-time badges until a page refresh.
    reconnectionAttempts: Infinity,
  });

  socket.on('connect_error', (error) => {
    // console.error('❌ Socket connection error:', error);
    
    // Provide helpful error messages
    if (error.message?.includes('CORS') || error.message?.includes('xhr poll error')) {
      // if (isDev) {
      //   console.warn('⚠️ CORS error detected. Make sure the proxy is configured in config/proxy.ts');
      //   console.warn('💡 The socket is trying to connect through the dev server proxy.');
      // } else {
      //   console.warn('⚠️ CORS error detected. The backend server needs to allow CORS for socket.io.');
      //   console.warn('💡 Backend should set Access-Control-Allow-Origin header for socket.io endpoint.');
      // }
    }
  });

  // NOTE: 'reconnect' / 'reconnect_error' / 'reconnect_failed' are Manager
  // events in socket.io v4 — they only fire on `socket.io`, never on the
  // socket itself (listening there is silently dead code).
  socket.io.on('reconnect', () => {
    if (socket?.connected) {
      socket.emit('subscribe:orders');
    }
  });

  return socket;
};

/**
 * Get current socket instance
 */
export const getSocket = (): Socket | null => {
  return socket;
};

/**
 * Disconnect socket
 */
export const disconnectSocket = (): void => {
  if (socket) {
    socket.disconnect();
    socket = null;
  }
};

/**
 * Subscribe to all order updates
 * This joins the 'orders' room to receive real-time order:status-counts events
 * Backend emits: io.to('orders').emit('order:status-counts', statusCounts)
 */
export const subscribeToOrders = (): void => {
  if (!socket) {
    // console.warn('⚠️ Socket not initialized. Call initSocket() first.');
    return;
  }

  // Only subscribe if connected
  if (socket.connected) {
    // Emit subscribe:orders to join the 'orders' room
    // Backend will join this socket to the 'orders' room and then emit order:status-counts
    socket.emit('subscribe:orders');
  } else {
    // console.warn('⚠️ Cannot subscribe: socket not connected yet');
    // Wait for connection, then subscribe
    socket.once('connect', () => {
      if (socket) {
        socket.emit('subscribe:orders');
      }
    });
  }
};

/**
 * Subscribe to specific order
 */
export const subscribeToOrder = (params: { orderId?: string; orderNumber?: string }): void => {
  if (!socket) {
    // console.warn('⚠️ Socket not initialized. Call initSocket() first.');
    return;
  }

  socket.emit('admin:subscribe:order', params);
};

/**
 * Unsubscribe from specific order
 */
export const unsubscribeFromOrder = (params: { orderId?: string; orderNumber?: string }): void => {
  if (!socket) {
    // console.warn('⚠️ Socket not initialized. Call initSocket() first.');
    return;
  }

  socket.emit('admin:unsubscribe:order', params);
};

/**
 * Update order via socket
 */
export const updateOrderViaSocket = (params: SocketUpdateOrderParams): void => {
  if (!socket) {
    // console.warn('⚠️ Socket not initialized. Call initSocket() first.');
    return;
  }

  socket.emit('admin:order:update', params);
};

/**
 * Batch update orders via socket
 */
export const batchUpdateOrdersViaSocket = (params: SocketBatchUpdateParams): void => {
  if (!socket) {
    // console.warn('⚠️ Socket not initialized. Call initSocket() first.');
    return;
  }

  socket.emit('admin:order:batch-update', params);
};

/**
 * Listen for order updates
 */
export const onOrderUpdate = (callback: (data: SocketOrderUpdate) => void): void => {
  if (!socket) {
    // console.warn('⚠️ Socket not initialized. Call initSocket() first.');
    return;
  }

  socket.on('order:update', callback);
};

/**
 * Request initial status counts via socket
 */
export const requestStatusCounts = (params?: {
  orderType?: API.OrderType | 'all';
  center?: API.CenterCode | 'all';
}): void => {
  if (!socket) {
    // console.warn('⚠️ Socket not initialized. Call initSocket() first.');
    return;
  }

  if (!socket.connected) {
    // console.warn('⚠️ Socket not connected. Waiting for connection...');
    socket.once('connect', () => {
      if (socket) {
        // socket.emit('admin:order:status-counts:request', params || { orderType: 'all', center: 'all' });
      }
    });
    return;
  }

  // socket.emit('admin:order:status-counts:request', params || { orderType: 'all', center: 'all' });
};

/**
 * Listen for status count updates from the 'orders' room
 * Backend emits: io.to('orders').emit('order:status-counts', statusCounts)
 * Note: This can be called multiple times - each callback will receive updates
 * Make sure to call subscribeToOrders() first to join the 'orders' room
 */
export const onStatusCountsUpdate = (callback: (data: API.OrderStatusCountsResponse['data']) => void): void => {
  if (!socket) {
    // console.warn('⚠️ Socket not initialized. Call initSocket() first.');
    return;
  }
  // Make sure subscribeToOrders() has been called to join the room first
  socket.on('order:status-counts', (data) => {
    callback(data);
  });
};

/**
 * Listen for new order notifications
 * Backend emits: io.to('orders').emit('order:new', orderData)
 * Make sure to call subscribeToOrders() first to join the 'orders' room
 */
export const onNewOrder = (callback: (data: any) => void): void => {
  if (!socket) {
    return;
  }
  socket.on('order:new', callback);
};

/**
 * Listen for update success
 */
export const onUpdateSuccess = (callback: (data: any) => void): void => {
  if (!socket) {
    return;
  }

  socket.on('admin:order:update:success', callback);
};

/**
 * Listen for update error
 */
export const onUpdateError = (callback: (error: any) => void): void => {
  if (!socket) {
    return;
  }

  socket.on('admin:order:update:error', callback);
};

/**
 * Listen for batch update success
 */
export const onBatchUpdateSuccess = (callback: (data: any) => void): void => {
  if (!socket) {
    return;
  }

  socket.on('admin:order:batch-update:success', callback);
};

/**
 * Listen for batch update error
 */
export const onBatchUpdateError = (callback: (error: any) => void): void => {
  if (!socket) {
    return;
  }

  socket.on('admin:order:batch-update:error', callback);
};

/**
 * Create inquiry for an order
 * Emits: admin:inquiry:create
 * Listens for: admin:inquiry:create:success or admin:inquiry:create:error
 */
export const createInquiry = (
  params: {
    orderId: string; // Required: Order ID to create inquiry for
    userId: string; // Required: User ID who owns the order
    message: string; // Required: Initial message content
    attachments?: Array<{
      type: 'image' | 'file' | 'video';
      url: string;
      name?: string;
    }>;
    autoAssign?: boolean; // Optional: Auto-assign to creating admin (default: true)
  },
  onSuccess?: (data: any) => void,
  onError?: (error: any) => void,
): void => {
  if (!socket) {
    return;
  }

  // Set up one-time listeners for success/error
  if (onSuccess) {
    socket.once('admin:inquiry:create:success', (data: any) => {
      onSuccess(data);
    });
  }

  if (onError) {
    socket.once('admin:inquiry:create:error', (error: any) => {
      onError(error);
    });
  }

  // Emit the request
  socket.emit('admin:inquiry:create', params);
};

/**
 * Get inquiries list via socket
 * Emits: admin:inquiry:list
 * Listens for: admin:inquiry:list:response or admin:inquiry:list:error
 */
export const getInquiries = (
  params: {
    status?: 'open' | 'closed' | 'resolved';
    assignedAdmin?: string;
    unassigned?: boolean;
    page?: number;
    pageSize?: number;
  },
  callback: (data: any) => void,
  onError?: (error: any) => void,
): void => {
  if (!socket) {
    return;
  }

  // Set up one-time listener for the response
  const responseHandler = (data: any) => {
    callback(data);
    // Remove listener after receiving response
    socket?.off('admin:inquiry:list:response', responseHandler);
  };

  const errorHandler = (error: any) => {
    if (onError) {
      onError(error);
    }
    socket?.off('admin:inquiry:list:error', errorHandler);
  };

  socket.once('admin:inquiry:list:response', responseHandler);
  if (onError) {
    socket.once('admin:inquiry:list:error', errorHandler);
  }

  // Emit the request
  socket.emit('admin:inquiry:list', params);
};

/**
 * Get single inquiry by ID
 * Emits: admin:inquiry:get
 * Listens for: admin:inquiry:get:response or admin:inquiry:get:error
 * Note: Automatically joins inquiry:{inquiryId} room upon success
 */
export const getInquiry = (
  params: {
    inquiryId: string; // Required: Inquiry ID
  },
  onSuccess: (data: any) => void,
  onError?: (error: any) => void,
): void => {
  if (!socket) {
    return;
  }

  // Set up one-time listeners for success/error
  socket.once('admin:inquiry:get:response', (data: any) => {
    onSuccess(data);
  });

  if (onError) {
    socket.once('admin:inquiry:get:error', (error: any) => {
      onError(error);
    });
  }

  // Emit the request
  socket.emit('admin:inquiry:get', params);
};

/**
 * Assign inquiry to admin via socket
 * Emits: admin:inquiry:assign
 * Listens for: admin:inquiry:assign:success or admin:inquiry:assign:error
 */
export const assignInquiry = (
  params: {
    inquiryId: string;
    adminId?: string; // optional, defaults to current admin
  },
  onSuccess?: (data: any) => void,
  onError?: (error: any) => void,
): void => {
  if (!socket) {
    return;
  }

  // Set up one-time listeners for success/error
  if (onSuccess) {
    socket.once('admin:inquiry:assign:success', (data: any) => {
      onSuccess(data);
    });
  }

  if (onError) {
    socket.once('admin:inquiry:assign:error', (error: any) => {
      onError(error);
    });
  }

  // Emit the request
  socket.emit('admin:inquiry:assign', params);
};

/**
 * Send message in inquiry via socket
 * Emits: admin:inquiry:send-message
 * Listens for: admin:inquiry:message:success or admin:inquiry:message:error
 */
export const sendInquiryMessage = (
  params: {
    inquiryId: string; // Required: Inquiry ID
    message: string; // Required: Message content
    attachments?: Array<{
      type: 'image' | 'file';
      url: string;
      name?: string;
    }>;
  },
  onSuccess?: (data: any) => void,
  onError?: (error: any) => void,
): void => {
  if (!socket) {
    return;
  }

  if (!params.inquiryId) {
    // console.error('❌ Cannot send message: inquiryId is required');
    if (onError) {
      onError({ message: 'inquiryId is required' });
    }
    return;
  }

  // Set up one-time listeners for success/error
  if (onSuccess) {
    socket.once('admin:inquiry:message:success', (data: any) => {
      onSuccess(data);
    });
  }

  if (onError) {
    socket.once('admin:inquiry:message:error', (error: any) => {
      onError(error);
    });
  }

  // Emit the request
  socket.emit('admin:inquiry:send-message', params);
};

/**
 * Subscribe to all inquiries
 * Emits: admin:inquiry:subscribe-all
 * Listens for: admin:inquiry:subscribe:success
 * Note: Joins admin:inquiries room to receive all inquiry-related broadcasts
 * Based on ADMIN_INQUIRY_INTEGRATION_GUIDE.md
 */
export const subscribeToInquiries = (
  onSuccess?: (data: any) => void,
  onError?: (error: any) => void,
): void => {
  if (!socket) {
    return;
  }

  if (onSuccess) {
    socket.once('admin:inquiry:subscribe:success', (data: any) => {
      onSuccess(data);
    });
  }

  if (onError) {
    socket.once('admin:inquiry:subscribe:error', (error: any) => {
      onError(error);
    });
  }

  // Use admin:inquiry:subscribe-all as per guide
  socket.emit('admin:inquiry:subscribe-all');
};

/**
 * Unsubscribe from all inquiries
 * Emits: admin:inquiry:unsubscribe-all
 * Listens for: admin:inquiry:unsubscribe:success
 * Based on ADMIN_INQUIRY_INTEGRATION_GUIDE.md
 */
export const unsubscribeFromInquiries = (
  onSuccess?: (data: any) => void,
): void => {
  if (!socket) {
    return;
  }

  if (onSuccess) {
    socket.once('admin:inquiry:unsubscribe:success', (data: any) => {
      onSuccess(data);
    });
  }

  // Use admin:inquiry:unsubscribe-all as per guide
  socket.emit('admin:inquiry:unsubscribe-all');
};

/**
 * Subscribe to specific inquiry
 * Emits: admin:inquiry:subscribe
 * Listens for: admin:inquiry:subscribe:success or admin:inquiry:subscribe:error
 * Based on ADMIN_INQUIRY_INTEGRATION_GUIDE.md
 */
export const subscribeToInquiry = (
  params: {
    inquiryId: string; // Required: Inquiry ID
  },
  onSuccess?: (data: any) => void,
  onError?: (error: any) => void,
): void => {
  if (!socket) {
    return;
  }

  if (onSuccess) {
    socket.once('admin:inquiry:subscribe:success', (data: any) => {
      onSuccess(data);
    });
  }

  if (onError) {
    socket.once('admin:inquiry:subscribe:error', (error: any) => {
      onError(error);
    });
  }

  // Use admin:inquiry:subscribe for specific inquiry as per guide
  socket.emit('admin:inquiry:subscribe', params);
};

/**
 * Unsubscribe from specific inquiry
 * Emits: admin:inquiry:unsubscribe
 * Listens for: admin:inquiry:unsubscribe:success or admin:inquiry:unsubscribe:error
 * Based on ADMIN_INQUIRY_INTEGRATION_GUIDE.md
 */
export const unsubscribeFromInquiry = (
  params: {
    inquiryId: string; // Required: Inquiry ID
  },
  onSuccess?: (data: any) => void,
  onError?: (error: any) => void,
): void => {
  if (!socket) {
    return;
  }

  if (onSuccess) {
    socket.once('admin:inquiry:unsubscribe:success', (data: any) => {
      onSuccess(data);
    });
  }

  if (onError) {
    socket.once('admin:inquiry:unsubscribe:error', (error: any) => {
      onError(error);
    });
  }

  // Use admin:inquiry:unsubscribe for specific inquiry as per guide
  socket.emit('admin:inquiry:unsubscribe', params);
};

/**
 * Mark messages as read
 * Emits: admin:inquiry:mark-read
 * Listens for: admin:inquiry:mark-read:success or admin:inquiry:mark-read:error
 */
export const markInquiryAsRead = (
  params: {
    inquiryId: string; // Required: Inquiry ID
  },
  onSuccess?: (data: any) => void,
  onError?: (error: any) => void,
): void => {
  if (!socket) {
    // console.warn('⚠️ Socket not initialized. Call initSocket() first.');
    return;
  }

  if (onSuccess) {
    socket.once('admin:inquiry:mark-read:success', (data: any) => {
      onSuccess(data);
    });
  }

  if (onError) {
    socket.once('admin:inquiry:mark-read:error', (error: any) => {
      // console.error('❌ Failed to mark messages as read:', error);
      onError(error);
    });
  }

  socket.emit('admin:inquiry:mark-read', params);
};

/**
 * Close inquiry
 * Emits: admin:inquiry:close
 * Listens for: admin:inquiry:close:success or admin:inquiry:close:error
 */
export const closeInquiry = (
  params: {
    inquiryId: string; // Required: Inquiry ID
    status?: 'closed' | 'resolved'; // Optional: Close status (default: 'closed')
  },
  onSuccess?: (data: any) => void,
  onError?: (error: any) => void,
): void => {
  if (!socket) {
    // console.warn('⚠️ Socket not initialized. Call initSocket() first.');
    return;
  }

  if (onSuccess) {
    socket.once('admin:inquiry:close:success', (data: any) => {
      onSuccess(data);
    });
  }

  if (onError) {
    socket.once('admin:inquiry:close:error', (error: any) => {
      // console.error('❌ Failed to close inquiry:', error);
      onError(error);
    });
  }

  socket.emit('admin:inquiry:close', params);
};

/**
 * Reopen inquiry
 * Emits: admin:inquiry:reopen
 * Listens for: admin:inquiry:reopen:success or admin:inquiry:reopen:error
 */
export const reopenInquiry = (
  params: {
    inquiryId: string; // Required: Inquiry ID
  },
  onSuccess?: (data: any) => void,
  onError?: (error: any) => void,
): void => {
  if (!socket) {
    // console.warn('⚠️ Socket not initialized. Call initSocket() first.');
    return;
  }

  if (onSuccess) {
    socket.once('admin:inquiry:reopen:success', (data: any) => {
      onSuccess(data);
    });
  }

  if (onError) {
    socket.once('admin:inquiry:reopen:error', (error: any) => {
      onError(error);
    });
  }

  socket.emit('admin:inquiry:reopen', params);
};

/**
 * Get unread count
 * Emits: admin:inquiry:unread-count
 * Listens for: admin:inquiry:unread-count:response or admin:inquiry:unread-count:error
 */
export const getUnreadCount = (
  callback: (data: any) => void,
  onError?: (error: any) => void,
): void => {
  if (!socket) {
    return;
  }

  socket.once('admin:inquiry:unread-count:response', (data: any) => {
    callback(data);
  });

  if (onError) {
    socket.once('admin:inquiry:unread-count:error', (error: any) => {
      onError(error);
    });
  }

  socket.emit('admin:inquiry:unread-count');
};

/**
 * Listen for inquiry message received (broadcast event)
 * Listens for: admin:inquiry:message:received (admin-specific) or inquiry:message:received (inquiry room)
 * Room: admin:inquiries or inquiry:{inquiryId}
 * Based on ADMIN_INQUIRY_INTEGRATION_GUIDE.md
 */
export const onInquiryMessageReceived = (callback: (data: any) => void): void => {
  if (!socket) {
    // console.warn('⚠️ Socket not initialized. Call initSocket() first.');
    return;
  }

  // Listen for admin-specific message event
  socket.on('admin:inquiry:message:received', callback);
  // Also listen for inquiry room message event (for backward compatibility)
  // socket.on('inquiry:message:received', callback);
};

/**
 * Listen for inquiry updates (broadcast event)
 * Listens for: admin:inquiry:updated
 * Room: admin:inquiries
 */
export const onInquiryUpdate = (callback: (data: any) => void): void => {
  if (!socket) {
    return;
  }

  socket.on('admin:inquiry:updated', callback);
};

/**
 * Listen for new inquiry notifications (broadcast event)
 * Listens for: admin:inquiry:new
 * Room: admin:inquiries
 */
export const onNewInquiry = (callback: (data: any) => void): void => {
  if (!socket) {
    // console.warn('⚠️ Socket not initialized. Call initSocket() first.');
    return;
  }

  socket.on('admin:inquiry:new', callback);
};

/**
 * Subscribe to deposit requests
 * Emits: admin:deposit-requests:subscribe-all or admin:deposit-requests:subscribe
 * Room: admin:deposit-requests
 */
export const subscribeToDepositRequests = (
  onSuccess?: (data: any) => void,
  onError?: (error: any) => void,
): void => {
  if (!socket) {
    return;
  }

  if (onSuccess) {
    socket.once('admin:deposit-requests:subscribe:success', (data: any) => {
      onSuccess(data);
    });
  }

  if (onError) {
    socket.once('admin:deposit-requests:subscribe:error', (error: any) => {
      onError(error);
    });
  }

  // Emit multiple variants for compatibility with backend semantics
  socket.emit('admin:deposit-requests:subscribe-all');
  socket.emit('admin:deposit-requests:subscribe');
  socket.emit('admin:subscribe:deposit-requests');
  socket.emit('subscribe:deposit-requests');
};

/**
 * Listen for new deposit request notifications
 * Event: admin:deposit-request:new
 * Room: admin:deposit-requests
 */
export const onDepositRequestNew = (callback: (data: any) => void): void => {
  if (!socket) {
    return;
  }

  socket.on('admin:deposit-request:new', callback);
  socket.on('deposit-request:new', callback);
};

/**
 * Listen for resolved deposit request notifications
 * Event: admin:deposit-request:resolved
 * Room: admin:deposit-requests
 */
export const onDepositRequestResolved = (callback: (data: any) => void): void => {
  if (!socket) {
    return;
  }

  socket.on('admin:deposit-request:resolved', callback);
  socket.on('deposit-request:resolved', callback);
};

/**
 * Listen for inquiry closed (broadcast event)
 * Listens for: admin:inquiry:closed (admin-specific) or inquiry:closed (inquiry room)
 * Room: admin:{adminId} or inquiry:{inquiryId}
 * Based on ADMIN_INQUIRY_INTEGRATION_GUIDE.md
 */
export const onInquiryClosed = (callback: (data: any) => void): void => {
  if (!socket) {
    // console.warn('⚠️ Socket not initialized. Call initSocket() first.');
    return;
  }

  // Listen for admin-specific closed event
  socket.on('admin:inquiry:closed', callback);
  // Also listen for inquiry room closed event (for backward compatibility)
  socket.on('inquiry:closed', callback);
};

/**
 * Listen for inquiry reopened (broadcast event)
 * Listens for: inquiry:reopened
 * Room: inquiry:{inquiryId}
 * Based on ADMIN_INQUIRY_INTEGRATION_GUIDE.md
 */
export const onInquiryReopened = (callback: (data: any) => void): void => {
  if (!socket) {
    // console.warn('⚠️ Socket not initialized. Call initSocket() first.');
    return;
  }

  socket.on('inquiry:reopened', callback);
};

/**
 * Listen for user read receipts (broadcast event)
 * Listens for: admin:inquiry:messages-read
 * Room: admin:{adminId} or admin:inquiries
 * Based on ADMIN_INQUIRY_INTEGRATION_GUIDE.md
 */
export const onInquiryMessagesRead = (callback: (data: any) => void): void => {
  if (!socket) {
    // console.warn('⚠️ Socket not initialized. Call initSocket() first.');
    return;
  }

  socket.on('admin:inquiry:messages-read', callback);
};

/**
 * Listen for inquiry assigned (broadcast event)
 * Listens for: admin:inquiry:assigned (admin-specific) or inquiry:admin-assigned (inquiry room)
 * Room: admin:{adminId} or inquiry:{inquiryId}
 * Based on ADMIN_INQUIRY_INTEGRATION_GUIDE.md
 */
export const onInquiryAssigned = (callback: (data: any) => void): void => {
  if (!socket) {
    // console.warn('⚠️ Socket not initialized. Call initSocket() first.');
    return;
  }

  // Listen for admin-specific assigned event
  socket.on('admin:inquiry:assigned', callback);
  // Also listen for inquiry room assigned event (for backward compatibility)
  socket.on('inquiry:admin-assigned', callback);
};

/**
 * Remove all listeners
 */
export const removeAllListeners = (): void => {
  if (!socket) {
    return;
  }

  socket.off('order:update');
  socket.off('order:status-counts');
  socket.off('order:new');
  socket.off('admin:order:update:success');
  socket.off('admin:order:update:error');
  socket.off('admin:order:batch-update:success');
  socket.off('admin:order:batch-update:error');
  socket.off('admin:inquiry:list:response');
  socket.off('admin:inquiry:list:error');
  socket.off('admin:inquiry:get:response');
  socket.off('admin:inquiry:get:error');
  socket.off('admin:inquiry:create:success');
  socket.off('admin:inquiry:create:error');
  socket.off('admin:inquiry:assign:success');
  socket.off('admin:inquiry:assign:error');
  socket.off('admin:inquiry:message:success');
  socket.off('admin:inquiry:message:error');
  socket.off('admin:inquiry:subscribe:success');
  socket.off('admin:inquiry:subscribe:error');
  socket.off('admin:inquiry:unsubscribe:success');
  socket.off('admin:inquiry:mark-read:success');
  socket.off('admin:inquiry:mark-read:error');
  socket.off('admin:inquiry:close:success');
  socket.off('admin:inquiry:close:error');
  socket.off('admin:inquiry:reopen:success');
  socket.off('admin:inquiry:reopen:error');
  socket.off('admin:inquiry:unread-count:response');
  socket.off('admin:inquiry:unread-count:error');
  socket.off('inquiry:message:received');
  socket.off('admin:inquiry:message:received');
  socket.off('admin:inquiry:updated');
  socket.off('admin:inquiry:new');
  socket.off('admin:inquiry:messages-read');
  socket.off('admin:inquiry:assigned');
  socket.off('admin:inquiry:closed');
  socket.off('inquiry:closed');
  socket.off('inquiry:reopened');
  socket.off('inquiry:admin-assigned');
  socket.off('admin:general-inquiry:list:response');
  socket.off('admin:general-inquiry:list:error');
  socket.off('admin:general-inquiry:get:response');
  socket.off('admin:general-inquiry:get:error');
  socket.off('admin:general-inquiry:create:success');
  socket.off('admin:general-inquiry:create:error');
  socket.off('admin:general-inquiry:assign:success');
  socket.off('admin:general-inquiry:assign:error');
  socket.off('admin:general-inquiry:message:success');
  socket.off('admin:general-inquiry:message:error');
  socket.off('admin:general-inquiry:mark-read:success');
  socket.off('admin:general-inquiry:mark-read:error');
  socket.off('admin:general-inquiry:close:success');
  socket.off('admin:general-inquiry:close:error');
  socket.off('admin:general-inquiry:reopen:success');
  socket.off('admin:general-inquiry:reopen:error');
  socket.off('admin:general-inquiry:unread-count:response');
  socket.off('admin:general-inquiry:unread-count:error');
  socket.off('admin:general-inquiry:new');
  socket.off('admin:general-inquiry:message:received');
  socket.off('general-inquiry:message:received');
  socket.off('admin:general-inquiry:assigned');
  socket.off('general-inquiry:admin-assigned');
  socket.off('admin:general-inquiry:closed');
  socket.off('general-inquiry:closed');
  socket.off('admin:general-inquiry:updated');
  socket.off('admin:general-inquiry:messages-read');
  socket.off('general-inquiry:messages-read');
  socket.off('admin:deposit-requests:subscribe:success');
  socket.off('admin:deposit-requests:subscribe:error');
  socket.off('admin:deposit-request:new');
  socket.off('deposit-request:new');
  socket.off('admin:deposit-request:resolved');
  socket.off('deposit-request:resolved');
  socket.off('admin:note:create:success');
  socket.off('admin:note:create:error');
  socket.off('admin:note:list:response');
  socket.off('admin:note:list:error');
  socket.off('admin:note:get:response');
  socket.off('admin:note:get:error');
  socket.off('admin:note:update:success');
  socket.off('admin:note:update:error');
  socket.off('admin:note:delete:success');
  socket.off('admin:note:delete:error');
  socket.off('note:broadcast');
  socket.off('note:deleted');
  socket.off('admin:deploy:notice');
  socket.off('admin:deploy:notice:cleared');
  socket.off('admin:coupon:stock-exhausted');
};

// ====================
// General Inquiry (1:1) Functions
// Based on GENERAL_INQUIRY_SOCKET_ENDPOINTS.md
// ====================

/**
 * Get General Inquiries list via socket
 * Emits: admin:general-inquiry:list
 * Listens for: admin:general-inquiry:list:response
 */
export const getGeneralInquiries = (
  params: {
    status?: 'open' | 'closed' | 'resolved';
    assignedAdmin?: string;
    unassigned?: boolean;
    category?: string;
    page?: number;
    pageSize?: number;
  },
  callback: (data: any) => void,
  onError?: (error: any) => void,
): void => {
  if (!socket) {
    // console.warn('[General Inquiry] getGeneralInquiries: socket not initialized');
    return;
  }

  const responseHandler = (data: any) => {
    const inquiries = data?.inquiries || [];
    callback(data);
    socket?.off('admin:general-inquiry:list:response', responseHandler);
  };

  const errorHandler = (error: any) => {
    // console.error('[General Inquiry] list:error', error);
    if (onError) {
      onError(error);
    }
    socket?.off('admin:general-inquiry:list:error', errorHandler);
  };

  socket.once('admin:general-inquiry:list:response', responseHandler);
  if (onError) {
    socket.once('admin:general-inquiry:list:error', errorHandler);
  }

  socket.emit('admin:general-inquiry:list', params);
};

/**
 * Get single General Inquiry by ID
 * Emits: admin:general-inquiry:get
 * Listens for: admin:general-inquiry:get:response
 */
export const getGeneralInquiry = (
  params: {
    inquiryId: string;
  },
  onSuccess: (data: any) => void,
  onError?: (error: any) => void,
): void => {
  if (!socket) {
    // console.warn('[General Inquiry] ⚠️ Socket not initialized. Call initSocket() first.');
    return;
  }

  socket.once('admin:general-inquiry:get:response', (data: any) => {
    onSuccess(data);
  });

  if (onError) {
    socket.once('admin:general-inquiry:get:error', (error: any) => {
      onError(error);
    });
  }

  socket.emit('admin:general-inquiry:get', params);
};

/**
 * Create General Inquiry for a user
 * Emits: admin:general-inquiry:create
 * Listens for: admin:general-inquiry:create:success
 */
export const createGeneralInquiry = (
  params: {
    userId: string;
    subject?: string;
    category?: 'general' | 'support' | 'complaint' | 'suggestion' | 'technical';
    message: string;
    attachments?: Array<{
      type: 'image' | 'file' | 'video';
      url: string;
      name?: string;
    }>;
    autoAssign?: boolean;
  },
  onSuccess?: (data: any) => void,
  onError?: (error: any) => void,
): void => {
  if (!socket) {
    // console.warn('[General Inquiry] ⚠️ Socket not initialized. Call initSocket() first.');
    return;
  }

  if (onSuccess) {
    socket.once('admin:general-inquiry:create:success', (data: any) => {
      onSuccess(data);
    });
  }

  if (onError) {
    socket.once('admin:general-inquiry:create:error', (error: any) => {
      onError(error);
    });
  }

  socket.emit('admin:general-inquiry:create', params);
};

/**
 * Assign General Inquiry to admin
 * Emits: admin:general-inquiry:assign
 * Listens for: admin:general-inquiry:assign:success
 */
export const assignGeneralInquiry = (
  params: {
    inquiryId: string;
    adminId?: string;
  },
  onSuccess?: (data: any) => void,
  onError?: (error: any) => void,
): void => {
  if (!socket) {
    // console.warn('[General Inquiry] ⚠️ Socket not initialized. Call initSocket() first.');
    return;
  }

  if (onSuccess) {
    socket.once('admin:general-inquiry:assign:success', (data: any) => {
      onSuccess(data);
    });
  }

  if (onError) {
    socket.once('admin:general-inquiry:assign:error', (error: any) => {
      onError(error);
    });
  }

  socket.emit('admin:general-inquiry:assign', params);
};

/**
 * Send message in General Inquiry
 * Emits: admin:general-inquiry:send-message
 * Listens for: admin:general-inquiry:message:success
 */
export const sendGeneralInquiryMessage = (
  params: {
    inquiryId: string;
    message: string;
    attachments?: Array<{
      type: 'image' | 'file' | 'video';
      url: string;
      name?: string;
    }>;
  },
  onSuccess?: (data: any) => void,
  onError?: (error: any) => void,
): void => {
  if (!socket) {
    // console.warn('[General Inquiry] sendGeneralInquiryMessage: socket not initialized');
    return;
  }

  if (!params.inquiryId) {
    // console.error('[General Inquiry] sendGeneralInquiryMessage: inquiryId is required');
    if (onError) {
      onError({ message: 'inquiryId is required' });
    }
    return;
  }
  if (onSuccess) {
    socket.once('admin:general-inquiry:message:success', (data: any) => {
      onSuccess(data);
    });
  }

  if (onError) {
    socket.once('admin:general-inquiry:message:error', (error: any) => {
      onError(error);
    });
  }

  socket.emit('admin:general-inquiry:send-message', params);
};

/**
 * Subscribe to all General Inquiries
 * Emits: admin:general-inquiry:subscribe
 * Listens for: admin:general-inquiry:subscribe:success
 */
export const subscribeToGeneralInquiries = (
  onSuccess?: (data: any) => void,
  onError?: (error: any) => void,
): void => {
  if (!socket) {
    // console.warn('[General Inquiry] ⚠️ Socket not initialized. Call initSocket() first.');
    return;
  }

  if (onSuccess) {
    socket.once('admin:general-inquiry:subscribe:success', (data: any) => {
      onSuccess(data);
    });
  }

  if (onError) {
    socket.once('admin:general-inquiry:subscribe:error', (error: any) => {
      onError(error);
    });
  }

  socket.emit('admin:general-inquiry:subscribe');
};

/**
 * Subscribe to specific General Inquiry
 * Emits: admin:general-inquiry:subscribe:inquiry
 */
export const subscribeToGeneralInquiry = (
  params: {
    inquiryId: string;
  },
  onSuccess?: (data: any) => void,
): void => {
  if (!socket) {
    return;
  }

  if (onSuccess) {
    socket.once('admin:general-inquiry:subscribe:success', (data: any) => {
      onSuccess(data);
    });
  }

  socket.emit('admin:general-inquiry:subscribe:inquiry', params);
};

/**
 * Mark General Inquiry messages as read
 * Emits: admin:general-inquiry:mark-read
 */
export const markGeneralInquiryAsRead = (
  params: {
    inquiryId: string;
  },
  onSuccess?: (data: any) => void,
  onError?: (error: any) => void,
): void => {
  if (!socket) {
    return;
  }

  if (onSuccess) {
    socket.once('admin:general-inquiry:mark-read:success', (data: any) => {
      onSuccess(data);
    });
  }

  if (onError) {
    socket.once('admin:general-inquiry:mark-read:error', (error: any) => {
      onError(error);
    });
  }

  socket.emit('admin:general-inquiry:mark-read', params);
};

/**
 * Close General Inquiry
 * Emits: admin:general-inquiry:close
 */
export const closeGeneralInquiry = (
  params: {
    inquiryId: string;
    status?: 'closed' | 'resolved';
  },
  onSuccess?: (data: any) => void,
  onError?: (error: any) => void,
): void => {
  if (!socket) {
    return;
  }

  if (onSuccess) {
    socket.once('admin:general-inquiry:close:success', (data: any) => {
      onSuccess(data);
    });
  }

  if (onError) {
    socket.once('admin:general-inquiry:close:error', (error: any) => {
      onError(error);
    });
  }

  socket.emit('admin:general-inquiry:close', params);
};

/**
 * Reopen General Inquiry
 * Emits: admin:general-inquiry:reopen
 */
export const reopenGeneralInquiry = (
  params: {
    inquiryId: string;
  },
  onSuccess?: (data: any) => void,
  onError?: (error: any) => void,
): void => {
  if (!socket) {
    // console.warn('[General Inquiry] ⚠️ Socket not initialized. Call initSocket() first.');
    return;
  }

  if (onSuccess) {
    socket.once('admin:general-inquiry:reopen:success', (data: any) => {
      onSuccess(data);
    });
  }

  if (onError) {
    socket.once('admin:general-inquiry:reopen:error', (error: any) => {
      onError(error);
    });
  }

  socket.emit('admin:general-inquiry:reopen', params);
};

/**
 * Get General Inquiry unread count
 * Emits: admin:general-inquiry:unread-count
 * Listens for: admin:general-inquiry:unread-count:response
 */
export const getGeneralInquiryUnreadCount = (
  callback: (data: any) => void,
  onError?: (error: any) => void,
): void => {
  if (!socket) {
    // console.warn('[General Inquiry] ⚠️ Socket not initialized. Call initSocket() first.');
    return;
  }

  socket.once('admin:general-inquiry:unread-count:response', (data: any) => {
    callback(data);
  });

  if (onError) {
    socket.once('admin:general-inquiry:unread-count:error', (error: any) => {
      onError(error);
    });
  }

  socket.emit('admin:general-inquiry:unread-count');
};

/**
 * Listen for new General Inquiry (broadcast event)
 * Listens for: admin:general-inquiry:new
 */
export const onGeneralInquiryNew = (callback: (data: any) => void): void => {
  if (!socket) {
    // console.warn('[General Inquiry] ⚠️ Socket not initialized. Call initSocket() first.');
    return;
  }

  socket.on('admin:general-inquiry:new', callback);
};

/**
 * Listen for General Inquiry message received (broadcast event)
 * Listens for: admin:general-inquiry:message:received
 */
export const onGeneralInquiryMessageReceived = (callback: (data: any) => void): void => {
  if (!socket) {
    return;
  }

  socket.on('admin:general-inquiry:message:received', callback);
  // socket.on('general-inquiry:message:received', callback);
};

/**
 * Listen for General Inquiry assigned (broadcast event)
 * Listens for: admin:general-inquiry:assigned
 */
export const onGeneralInquiryAssigned = (callback: (data: any) => void): void => {
  if (!socket) {
    return;
  }

  socket.on('admin:general-inquiry:assigned', callback);
  socket.on('general-inquiry:admin-assigned', callback);
};

/**
 * Listen for General Inquiry closed (broadcast event)
 * Listens for: admin:general-inquiry:closed
 */
export const onGeneralInquiryClosed = (callback: (data: any) => void): void => {
  if (!socket) {
    // console.warn('[General Inquiry] ⚠️ Socket not initialized. Call initSocket() first.');
    return;
  }

  socket.on('admin:general-inquiry:closed', callback);
  socket.on('general-inquiry:closed', callback);
};

/**
 * Listen for General Inquiry updated (broadcast event)
 * Listens for: admin:general-inquiry:updated
 */
export const onGeneralInquiryUpdated = (callback: (data: any) => void): void => {
  if (!socket) {
    return;
  }

  socket.on('admin:general-inquiry:updated', callback);
};

/**
 * Listen for General Inquiry messages read (broadcast event)
 * Listens for: admin:general-inquiry:messages-read
 */
export const onGeneralInquiryMessagesRead = (callback: (data: any) => void): void => {
  if (!socket) {
    // console.warn('[General Inquiry] ⚠️ Socket not initialized. Call initSocket() first.');
    return;
  }

  socket.on('admin:general-inquiry:messages-read', callback);
  socket.on('general-inquiry:messages-read', callback);
};

// ==================== Note (Broadcast) API ====================

/**
 * Create Note and broadcast to all users
 * Emits: admin:note:create
 * Listens for: admin:note:create:success, admin:note:create:error
 */
export const createNote = (
  params: {
    type: 'announcement' | 'maintenance' | 'update' | 'warning' | 'info' | 'promotion';
    content: string;
    expiresAt?: string;
    priority?: 'low' | 'normal' | 'high' | 'urgent';
    targetAudience?: 'all' | 'users' | 'admins';
  },
  onSuccess?: (data: any) => void,
  onError?: (error: any) => void,
): void => {
  if (!socket) {
    return;
  }

  if (onSuccess) {
    socket.once('admin:note:create:success', (data: any) => {
      onSuccess(data);
    });
  }

  if (onError) {
    socket.once('admin:note:create:error', (error: any) => {
      onError(error);
    });
  }

  socket.emit('admin:note:create', params);
};

/**
 * Get Notes List (paginated)
 * Emits: admin:note:list
 * Listens for: admin:note:list:response, admin:note:list:error
 */
export const getNotes = (
  params?: {
    type?: 'announcement' | 'maintenance' | 'update' | 'warning' | 'info' | 'promotion';
    isActive?: boolean;
    priority?: 'low' | 'normal' | 'high' | 'urgent';
    targetAudience?: 'all' | 'users' | 'admins';
    page?: number;
    pageSize?: number;
    /** 관리자 표 머리글 정렬(서버가 전체를 정렬한 뒤 페이지를 자른다) */
    sortField?: string;
    sortOrder?: 'asc' | 'desc';
  },
  callback?: (data: any) => void,
  onError?: (error: any) => void,
): void => {
  if (!socket) {
    return;
  }

  if (callback) {
    socket.once('admin:note:list:response', (data: any) => {
      callback(data);
    });
  }

  if (onError) {
    socket.once('admin:note:list:error', (error: any) => {
      onError(error);
    });
  }

  socket.emit('admin:note:list', params || {});
};

/**
 * Get Single Note by ID
 * Emits: admin:note:get
 * Listens for: admin:note:get:response, admin:note:get:error
 */
export const getNote = (
  noteId: string,
  callback?: (data: any) => void,
  onError?: (error: any) => void,
): void => {
  if (!socket) {
    return;
  }

  if (callback) {
    socket.once('admin:note:get:response', (data: any) => {
      callback(data);
    });
  }

  if (onError) {
    socket.once('admin:note:get:error', (error: any) => {
      onError(error);
    });
  }

  socket.emit('admin:note:get', { noteId });
};

/**
 * Update Note
 * Emits: admin:note:update
 * Listens for: admin:note:update:success, admin:note:update:error
 */
export const updateNote = (
  params: {
    noteId: string;
    type?: 'announcement' | 'maintenance' | 'update' | 'warning' | 'info' | 'promotion';
    content?: string;
    expiresAt?: string;
    priority?: 'low' | 'normal' | 'high' | 'urgent';
    targetAudience?: 'all' | 'users' | 'admins';
  },
  onSuccess?: (data: any) => void,
  onError?: (error: any) => void,
): void => {
  if (!socket) {
    return;
  }

  if (onSuccess) {
    socket.once('admin:note:update:success', (data: any) => {
      onSuccess(data);
    });
  }

  if (onError) {
    socket.once('admin:note:update:error', (error: any) => {
      onError(error);
    });
  }

  socket.emit('admin:note:update', params);
};

/**
 * Delete Note (soft delete - sets isActive to false)
 * Emits: admin:note:delete
 * Listens for: admin:note:delete:success, admin:note:delete:error
 */
export const deleteNote = (
  noteId: string,
  onSuccess?: (data: any) => void,
  onError?: (error: any) => void,
): void => {
  if (!socket) {
    return;
  }

  if (onSuccess) {
    socket.once('admin:note:delete:success', (data: any) => {
      onSuccess(data);
    });
  }

  if (onError) {
    socket.once('admin:note:delete:error', (error: any) => {
      onError(error);
    });
  }

  socket.emit('admin:note:delete', { noteId });
};

/**
 * Listen for Note Broadcast (real-time)
 * Listens for: note:broadcast
 */
export const onNoteBroadcast = (callback: (data: any) => void): void => {
  if (!socket) {
    return;
  }

  socket.on('note:broadcast', callback);
};

/**
 * Listen for Note Deletion Notification
 * Listens for: note:deleted
 */
export const onNoteDeleted = (callback: (data: any) => void): void => {
  if (!socket) {
    return;
  }

  socket.on('note:deleted', callback);
};

/**
 * Admin Pending Counts — real-time deposit notification counts
 */
export interface AdminPendingCounts {
  bankTransferPendingCount: number;
  /** 입금·출금 예치금 신청 대기 합계 */
  depositRequestPendingCount?: number;
  /** @deprecated 서버가 `depositRequestPendingCount`를 내려주면 그것을 쓰세요 */
  depositRechargePendingCount?: number;
}

/** Request the server to emit current pending counts */
export const requestAdminPendingCounts = (): void => {
  if (!socket) return;
  socket.emit('admin:pending-counts:request');
};

/** Subscribe to pending counts response/updates */
export const onAdminPendingCounts = (callback: (data: AdminPendingCounts) => void): void => {
  if (!socket) return;
  socket.on('admin:pending-counts:response', callback);
};

/** Unsubscribe from pending counts */
export const offAdminPendingCounts = (callback: (data: AdminPendingCounts) => void): void => {
  if (!socket) return;
  socket.off('admin:pending-counts:response', callback);
};

// ==================== Deploy Notice (server update banner) ====================

export interface DeployNotice {
  message: string;
  startedAt?: string;
  /** ISO 8601 — expected end time (display only) */
  expiresAt?: string;
}

/**
 * Listen for the deploy-notice banner event.
 * Event: admin:deploy:notice — fired before a backend deploy (and re-sent on
 * reconnect while still active). Payload: { notice: DeployNotice }.
 */
export const onDeployNotice = (
  callback: (data: { notice: DeployNotice }) => void,
): void => {
  if (!socket) return;
  socket.on('admin:deploy:notice', callback);
};

export const offDeployNotice = (
  callback: (data: { notice: DeployNotice }) => void,
): void => {
  if (!socket) return;
  socket.off('admin:deploy:notice', callback);
};

/**
 * Listen for the deploy-notice cleared event.
 * Event: admin:deploy:notice:cleared — fired after the deploy completes.
 * Payload: { timestamp?: string }.
 */
export const onDeployNoticeCleared = (
  callback: (data: { timestamp?: string }) => void,
): void => {
  if (!socket) return;
  socket.on('admin:deploy:notice:cleared', callback);
};

export const offDeployNoticeCleared = (
  callback: (data: { timestamp?: string }) => void,
): void => {
  if (!socket) return;
  socket.off('admin:deploy:notice:cleared', callback);
};

// ==================== Coupon stock exhausted (checkout blocked) ====================

/**
 * A member's checkout was refused because a coupon's issued stock ran out.
 * The member cannot fix this — only an operator can raise the quantity or end the campaign.
 */
export interface CouponStockExhaustedAlert {
  /** Stable per member+coupon, so repeat attempts collapse into one alert. */
  alertId: string;
  userId: string;
  userName: string;
  userUniqueId: string;
  /** Empty when the order rolled back before a number was assigned. */
  orderNumber: string;
  couponId: string;
  couponName: string;
  couponCode: string;
  couponType: string;
  remainingQuantity: number;
  requestedQuantity: number;
  items: Array<{ title: string; offerId: string; quantity: number }>;
  totalItemCount: number;
  orderTotalKRW: number;
  timestamp?: string;
}

/** Event: admin:coupon:stock-exhausted — room `admins` (joined automatically on admin auth). */
export const onCouponStockExhausted = (
  callback: (data: CouponStockExhaustedAlert) => void,
): void => {
  if (!socket) return;
  socket.on('admin:coupon:stock-exhausted', callback);
};

export const offCouponStockExhausted = (
  callback: (data: CouponStockExhaustedAlert) => void,
): void => {
  if (!socket) return;
  socket.off('admin:coupon:stock-exhausted', callback);
};


